import type {
  TMajorActionDirectionalRentData,
  TPlayerId,
} from '@kapitalist/contract/types'
import type {TMillions} from '@kapitalist/contract/types/millions'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import {skins} from '~/game/skins'
import {DirectionalRentCard} from '~/models/game/Cards/RentCard'
import {PlayerGamePosition} from '~/models/game/GamePosition'
import type {TMajorActionUserFacingDescriptionData} from '~/models/game/MajorAction'
import MajorActionRentBase from '~/models/game/MajorActions/MajorActionRentBase'
import type Portfolio from '~/models/game/Portfolio'
import type StandardPortfolioNeighborhood from '~/models/game/PortfolioNeighborhoods/StandardPortfolioNeighborhood'
import {standardPNForIdThrows} from '~/models/game/utils'
import {sentenceCase, xVerbY} from '~/utils/language'

type PublicMembersFromApi = Pick<
  TMajorActionDirectionalRentData,
  'minorActorPlayerId'
>

export default class MajorActionDirectionalRent
  extends MajorActionRentBase<TMajorActionDirectionalRentData>
  implements PublicMembersFromApi
{
  card: DirectionalRentCard
  minorActorPlayerId: TPlayerId // target of the rent
  millionsOwed: TMillions

  // which neighborhood was used to initiate the rent
  public portfolioNeighborhood: StandardPortfolioNeighborhood

  constructor(data: TMajorActionDirectionalRentData, portfolio: Portfolio) {
    super(data)
    this.card = new DirectionalRentCard(data.card)

    this.portfolioNeighborhood = standardPNForIdThrows(portfolio)(
      data.portfolioNeighborhoodId
    )
    this.minorActorPlayerId = data.minorActorPlayerId
    this.millionsOwed = data.millionsOwed
  }

  public userFacingDescription({
    tense,
    skin,
    gamePosition,
  }: TMajorActionUserFacingDescriptionData): string {
    const currentPlayer =
      gamePosition instanceof PlayerGamePosition
        ? gamePosition.currentPlayer
        : undefined
    const majorActor = gamePosition.majorActorPlayer
    const minorActor = gamePosition.players.find(
      o => o.id === this.minorActorPlayerId
    )
    if (!minorActor) {
      throw new Error(
        `Minor actor not found in MajorActionDirectionalRent description`
      )
    }

    const color = this.portfolioNeighborhood.displayColorShort(skin)
    const dropFirstNoun = tense === 'imperativePrompt'

    const {conjugatedVerb, xNoun, yNoun} = xVerbY(
      tense,
      majorActor,
      dropFirstNoun ? 'Play' : 'play',
      minorActor,
      currentPlayer?.id
    )

    const actor = dropFirstNoun
      ? undefined
      : xNoun === majorActor.username
        ? xNoun
        : sentenceCase(xNoun)
    const shortfall =
      minorActor.portfolio.totalValue() < this.millionsOwed
        ? `(${show$(minorActor.portfolio.totalValue())})`
        : undefined

    return skins[skin].gameLogDescriptions.majorActions.directionalRent({
      tense,
      actor,
      playVerb: conjugatedVerb,
      amount: show$(this.millionsOwed),
      shortfall,
      cardName: this.card.displayName(skin),
      color,
      targetName: yNoun,
    })
  }
}
